import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from '../components/Header';
import {
  Search, Add, Storefront, CheckCircle,
  CalendarToday, ExpandMore, Visibility, MoreHoriz, Edit,
  Bolt, MusicNote, ShoppingCart, Flight, Keyboard, Wallet, Coffee,
  SyncProblem, AddPhotoAlternate, Download, Sync, Delete, Close,
  ChevronLeft, ChevronRight, AutoAwesome
} from '@mui/icons-material';

interface Asset {
  id: string;
  title: string;
  image: string;
  score: number;
  status: 'Published' | 'Draft' | 'Failed';
  date: string; // "Oct 24, 2023"
  platform: 'Amazon' | 'Ozon' | 'Shopify' | 'AliExpress';
  icon: React.ReactNode;
  iconColor: string;
}

export const AssetManagementScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set([]));

  // Filter States
  const [platformFilter, setPlatformFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [dateFilter, setDateFilter] = useState<string>('Last 30 Days');
  const [searchQuery, setSearchQuery] = useState('');

  // Dropdown Visibility States
  const [openPlatformMenu, setOpenPlatformMenu] = useState(false);
  const [openStatusMenu, setOpenStatusMenu] = useState(false);
  const [openDateMenu, setOpenDateMenu] = useState(false);

  // Mock Data
  const assets: Asset[] = [
    {
      id: '1',
      title: 'Ergonomic Mesh Office Chair Pro',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuCQsj4PL5J1jzB_FviL8vYI7sy1b5fPgkfIHr8lh6nXwXw9OrLuU6f0lhVuDV2AEz3t9ecKW6xkYiJ1fpuFUFdBI00gdpESH7brwbDaUBb4oAV7tYoDYk3oSqNQAKe5mjNBjKeJBxk3cT4lurO4MGCeAgogdcaUrzDaISE4i7iPwPtTqZhMIYeLhHxIV6eftcKgae5yiudDzcevcP6xSJvMKFCM6XPyng47O3KyE0Kx-lQPteTaqF6jp5qPKajMbdjiWQwl9lT_ssNZ',
      score: 98,
      status: 'Published',
      date: 'Oct 24, 2023',
      platform: 'Ozon',
      icon: <ShoppingCart />,
      iconColor: 'text-primary'
    },
    {
      id: '2',
      title: 'Wireless Noise-Canceling Headphones',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAX9tRrXWpASo8gKaNW7FHVUeQXgi3foBNDzAW1cc3dVYmo1mDoLsz-_vE020iMgeFhXx-SyDRfZN4_bpUphFM3cthPv4Rv2QpJQoo8Y4EO7JiI8c4h9YepPnI_Ff_GCn-Zj9uGxFJlr9Qk7nK_X_1sWNXOQwsg3qpl3SNdLqQy2cYVLwOo5YjPloj4-Ob61Eotg8rRC_VU1T5xQ_VK2zDKWWI9435r7ejHzSpEKGseHmAzLw1bh1JjzX3qL08yEf_Khakg47qdBtqX',
      score: 92,
      status: 'Draft',
      date: 'Oct 23, 2023',
      platform: 'Amazon',
      icon: <MusicNote />,
      iconColor: 'text-pink-600'
    },
    {
      id: '3',
      title: 'Smart LED Minimalist Desk Lamp',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAz5a1lzvQuheMdwDh2JxWBzgLHUDT-Tpx-_qkQB5E4MZ26JL2o539K4VHTkkEF-wAyJysRlf2AQFRzk14bqI3UsR56NfKWe9Bn0TNvrzIgj8i0MWJ3mbtd6-m3BS19JdOH3mXHtCr0ER4qTG4qHYxT7TBqac68rffKtWL994MGrXr-ofiMAlXNj8oAQldLrpW3hMJgmNxnEx-pPfSrs4-vzeFh9yDz_YeZSAZPqXhVIrw_MBgmr7fpxmBIamYniUI0g-kxxM2_SqvA',
      score: 85,
      status: 'Published',
      date: 'Oct 22, 2023',
      platform: 'Shopify',
      icon: <Bolt />,
      iconColor: 'text-green-600'
    },
    {
      id: '4',
      title: 'Portable Espresso Maker V2',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuAeaakmhmwk5VibvPR5rKaS2ulagJqXe8mWUIdG9-iQduulnueKmy1zM3iqMrmVE3my1EVIA-LUaifQ2wb40qpX216bvObs9abRVikQxryz1v6z_49rkwfPjYvcAKR9VRQaSxmoianpqEj0vwbGrSi6be3Mg2Orc93w9p0tKJ0a4JrOj51S6hpYMoCRKLIp_LAVDhRgzy3Pk4l6u8uvGhMXfH9w7_erGI6Szi-TqspQoKYwwYBGFQl5LY4-Y_WsEWiJ-hlJnwekoZWm',
      score: 95,
      status: 'Failed',
      date: 'Oct 21, 2023',
      platform: 'Ozon',
      icon: <Coffee />,
      iconColor: 'text-orange-600'
    },
    {
      id: '5',
      title: 'Minimalist Leather Wallet Slim',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDCRne-qnPL2D6kYhH028hBISOn3fi2-XLinNb6xDU2-PK9ziUlSNqwL6F-FsyJJDRtCY_aatnGtd-LXZKvQtstichdne987zGfNWCJ4sBr5jU_cySD_helSa8Hf8Xb50Amhot5aupTnYB9PqX-OL03CCa-HChc8nRdB1ObNY5CBGmcTGtqiycPIte0J8C_j0rTxy0Q6_HwAOiTdbn8XkiWFXCkykFE_oyX8ebWk_JqNwE5XDWIsW0Jx6-hDTl3Lsenali3VZHQFRjc',
      score: 88,
      status: 'Published',
      date: 'Oct 20, 2023',
      platform: 'AliExpress',
      icon: <Wallet />,
      iconColor: 'text-blue-600'
    },
    {
      id: '6',
      title: 'Mechanical Gaming Keyboard RGB',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuDUPA4MAgYHr1V9k85LU81izCDpVQUh7x5-pXgsHWAQGw5hIP5h7Gubx-oGtcEXUbUFzMDY0XhjCUHyXdlZF9QAjzutFpCvKm4usO44rDnZzfkP5h9TiKCgj35YHGUg7WhObOd-EbqGDXasrCNgH4SCMQwaxrVcS3NXJc2b6AA4OSXE7yRta7bQ_xxh_qO1a-3CMytSCWOFmclnvbIDSNOss1-sB2AjXOhVNTPbI2dHfRcjWHCBUXUfNEwPE7W7L9Jq60ZB1er2mAKd',
      score: 90,
      status: 'Draft',
      date: 'Oct 19, 2023',
      platform: 'Ozon',
      icon: <Keyboard />,
      iconColor: 'text-purple-600'
    },
    {
      id: '7',
      title: 'Compact 4K Camera Drone Pro',
      image: 'https://lh3.googleusercontent.com/aida-public/AB6AXuC4nnITzlkvWX3A-A8bxncpt91wTmumd_ToPDf99x-NQDDkQlIUjLVEO3pywWpFbxFTmnVARea2NqmexsIKPDmDCrVLjDFZZZjoDOu5LeUzIKrui50jrhkbuxzi76ekPK6vYgQ7jhZdWIbmA_sIC-m3vrEa8wLTMRWDB_M77XXrq4KEaP80S6Bnv7jBD5l6uPEf_e7cs8GgDnCTqEr2_yYzTPJRgz8Lwd3uvR9sLxy7PYPVQSyYD6Zyqf6i-LUOKkTNBHw01jyf8H2b',
      score: 96,
      status: 'Published',
      date: 'Oct 18, 2023',
      platform: 'Amazon',
      icon: <Flight />,
      iconColor: 'text-sky-600'
    }
  ];

  const filteredAssets = useMemo(() => {
    return assets.filter(asset => {
      // Search Query
      if (searchQuery && !asset.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      // Platform Filter
      if (platformFilter !== 'All' && asset.platform !== platformFilter) {
        return false;
      }
      // Status Filter
      if (statusFilter !== 'All' && asset.status !== statusFilter) {
        return false;
      }
      // Date Filter (Simplified logic for mock data)
      if (dateFilter === 'Last 7 Days') {
         // Just a mock check, assume Oct 20-24 is within last 7 days from "today" (simulated)
         const day = parseInt(asset.date.split(' ')[1]);
         if (day < 18) return false;
      }
      return true;
    });
  }, [assets, searchQuery, platformFilter, statusFilter, dateFilter]);

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedAssets);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAssets(newSelected);
  };

  const clearFilters = () => {
    setPlatformFilter('All');
    setStatusFilter('All');
    setDateFilter('Last 30 Days');
    setSearchQuery('');
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'Published': return 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'Draft': return 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400';
      case 'Failed': return 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400';
    }
  };

  const getStatusDot = (status: Asset['status']) => {
    switch (status) {
      case 'Published': return 'bg-emerald-500';
      case 'Draft': return 'bg-amber-500';
      case 'Failed': return 'bg-red-500';
    }
  };

  const getStatusText = (status: Asset['status']) => {
    switch (status) {
      case 'Published': return t('assets.published');
      case 'Draft': return t('assets.draft');
      case 'Failed': return t('assets.failed');
    }
  };

  const rightContent = (
    <div className="flex items-center gap-4">
      <label className="relative hidden sm:block w-full max-w-xs">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
          <Search />
        </span>
        <input
          className="w-full pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-none rounded-lg text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none placeholder:text-slate-500"
          placeholder={t('assets.searchPlaceholder')}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </label>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#111621] text-slate-900 dark:text-slate-100 pb-24" onClick={() => {
        setOpenPlatformMenu(false);
        setOpenStatusMenu(false);
        setOpenDateMenu(false);
    }}>
      {/* Header with Navigation */}
      <Header
        activeNav="assets"
        rightElement={rightContent}
      />

      <main className="max-w-[1200px] mx-auto px-6 py-8">
        {/* Page Title & CTA */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">Asset Management</h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Manage and sync your AI-optimized product listings.</p>
          </div>
          <button onClick={() => navigate('/import')} className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg font-bold text-sm shadow-sm hover:bg-blue-700 transition-all active:scale-95">
            <Add fontSize="small" />
            New Optimization
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-8 bg-white dark:bg-slate-900 p-2 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm animate-in fade-in slide-in-from-top-2 duration-500 z-20 relative">

          {/* Platform Filter */}
          <div className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setOpenPlatformMenu(!openPlatformMenu); setOpenStatusMenu(false); setOpenDateMenu(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all active:scale-95 ${platformFilter !== 'All' ? 'bg-primary/10 border-primary text-primary' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary'}`}
            >
                <Storefront fontSize="small" />
                {platformFilter === 'All' ? 'Platform: All' : platformFilter}
                <ExpandMore fontSize="small" className="text-slate-400" />
            </button>
            {openPlatformMenu && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {['All', 'Ozon', 'Amazon', 'Shopify', 'AliExpress'].map((p) => (
                        <button
                            key={p}
                            onClick={() => setPlatformFilter(p)}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${platformFilter === p ? 'font-bold text-primary bg-primary/5' : 'text-slate-600 dark:text-slate-300'}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            )}
          </div>

          {/* Status Filter */}
          <div className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setOpenStatusMenu(!openStatusMenu); setOpenPlatformMenu(false); setOpenDateMenu(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all active:scale-95 ${statusFilter !== 'All' ? 'bg-primary/10 border-primary text-primary' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary'}`}
            >
                <CheckCircle fontSize="small" className={statusFilter !== 'All' ? 'text-primary' : 'text-emerald-500'} />
                {statusFilter === 'All' ? 'Status: All' : statusFilter}
                <ExpandMore fontSize="small" className="text-slate-400" />
            </button>
            {openStatusMenu && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {['All', 'Published', 'Draft', 'Failed'].map((s) => (
                        <button
                            key={s}
                            onClick={() => setStatusFilter(s)}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${statusFilter === s ? 'font-bold text-primary bg-primary/5' : 'text-slate-600 dark:text-slate-300'}`}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
          </div>

          {/* Date Filter */}
          <div className="relative">
            <button
                onClick={(e) => { e.stopPropagation(); setOpenDateMenu(!openDateMenu); setOpenPlatformMenu(false); setOpenStatusMenu(false); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all active:scale-95 ${dateFilter !== 'Last 30 Days' ? 'bg-primary/10 border-primary text-primary' : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-primary'}`}
            >
                <CalendarToday fontSize="small" />
                {dateFilter}
                <ExpandMore fontSize="small" className="text-slate-400" />
            </button>
            {openDateMenu && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                    {['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'All Time'].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDateFilter(d)}
                            className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors ${dateFilter === d ? 'font-bold text-primary bg-primary/5' : 'text-slate-600 dark:text-slate-300'}`}
                        >
                            {d}
                        </button>
                    ))}
                </div>
            )}
          </div>

          <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
          <button
            onClick={clearFilters}
            className="text-slate-500 text-sm font-medium hover:text-primary transition-colors"
          >
            Clear Filters
          </button>
        </div>

        {/* Asset Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredAssets.length > 0 ? filteredAssets.map((asset, index) => (
            <div
                key={asset.id}
                onClick={() => toggleSelect(asset.id)}
                className={`group relative bg-white dark:bg-slate-900 border rounded-xl overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer animate-in fade-in zoom-in-95 ${selectedAssets.has(asset.id) ? 'border-primary ring-1 ring-primary' : 'border-slate-200 dark:border-slate-800'}`}
                style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="absolute top-3 left-3 z-10" onClick={(e) => e.stopPropagation()}>
                <input
                    type="checkbox"
                    checked={selectedAssets.has(asset.id)}
                    onChange={() => toggleSelect(asset.id)}
                    className="rounded border-slate-300 text-primary focus:ring-primary w-5 h-5 cursor-pointer accent-primary"
                />
              </div>
              <div className="relative aspect-square overflow-hidden bg-slate-100 dark:bg-slate-800">
                <img
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    alt={asset.title}
                    src={asset.image}
                />
                <div className="absolute top-3 right-3 flex flex-col gap-2">
                  <span className="bg-white/90 backdrop-blur-md text-slate-900 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 shadow-sm">
                    <AutoAwesome className="text-primary" style={{ fontSize: 14 }} />
                    Score {asset.score}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-bold text-slate-900 dark:text-white leading-tight line-clamp-2 text-sm">{asset.title}</h3>
                  <div className={asset.iconColor}>
                    {React.cloneElement(asset.icon as React.ReactElement, { fontSize: 'small' } as any)}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full font-bold ${getStatusColor(asset.status)}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${getStatusDot(asset.status)}`}></span>
                    {getStatusText(asset.status)}
                  </span>
                  <span>•</span>
                  <span>{asset.date}</span>
                </div>
                <div className="flex items-center gap-2 border-t border-slate-100 dark:border-slate-800 pt-4" onClick={(e) => e.stopPropagation()}>
                  {asset.status === 'Failed' ? (
                    <button className="flex-1 py-2 bg-slate-50 dark:bg-slate-800 hover:bg-primary hover:text-white text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-1 group/btn">
                        <SyncProblem fontSize="small" className="group-hover/btn:animate-spin" />
                        {t('assets.retry')}
                    </button>
                  ) : asset.status === 'Draft' ? (
                    <button className="flex-1 py-2 bg-slate-50 dark:bg-slate-800 hover:bg-primary hover:text-white text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-1">
                        <Edit fontSize="small" />
                        {t('assets.edit')}
                    </button>
                  ) : (
                    <button className="flex-1 py-2 bg-slate-50 dark:bg-slate-800 hover:bg-primary hover:text-white text-slate-700 dark:text-slate-300 rounded-lg text-sm font-bold transition-colors flex items-center justify-center gap-1">
                        <Visibility fontSize="small" />
                        {t('assets.view')}
                    </button>
                  )}
                  <button className="p-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition-colors">
                    <MoreHoriz fontSize="small" />
                  </button>
                </div>
              </div>
            </div>
          )) : (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-center opacity-70">
                <div className="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                    <Search className="text-slate-400" style={{ fontSize: 40 }} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">No assets found</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Try adjusting your filters or search query.</p>
                <button onClick={clearFilters} className="mt-4 text-primary font-bold text-sm hover:underline">Clear all filters</button>
            </div>
          )}

          {/* Add More Card (Only show if no filters active or minimal filters) */}
          {filteredAssets.length > 0 && (
            <button
                onClick={() => navigate('/import')}
                className="group bg-slate-50 dark:bg-slate-900/50 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl flex flex-col items-center justify-center p-8 hover:border-primary/50 hover:bg-primary/5 transition-all min-h-[350px] animate-in fade-in zoom-in-95"
                style={{ animationDelay: '400ms' }}
            >
                <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-primary transition-colors">
                <AddPhotoAlternate style={{ fontSize: 28 }} />
                </div>
                <p className="mt-4 font-bold text-slate-900 dark:text-slate-300">Add New Listing</p>
                <p className="text-xs text-slate-500 text-center mt-2 max-w-[150px]">Upload raw photos for AI enhancement.</p>
            </button>
          )}
        </div>

        {/* Floating Batch Action Bar */}
        {selectedAssets.size > 0 && (
            <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-10 fade-in duration-300">
                <div className="bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-8 border border-slate-700/50 backdrop-blur-xl">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center font-bold text-sm">
                            {selectedAssets.size}
                        </div>
                        <span className="text-sm font-semibold whitespace-nowrap">Assets Selected</span>
                    </div>
                    <div className="h-6 w-px bg-slate-700"></div>
                    <div className="flex items-center gap-2">
                        <button className="flex items-center gap-2 px-4 py-2 hover:bg-slate-800 rounded-lg text-sm font-bold transition-all active:scale-95">
                            <Download fontSize="small" />
                            Batch Download
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 hover:bg-slate-800 rounded-lg text-sm font-bold transition-all active:scale-95">
                            <Sync fontSize="small" />
                            Batch Sync
                        </button>
                        <button className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-400/10 rounded-lg text-sm font-bold transition-all active:scale-95">
                            <Delete fontSize="small" />
                            Delete
                        </button>
                    </div>
                    <button
                        onClick={() => setSelectedAssets(new Set())}
                        className="ml-4 text-slate-400 hover:text-white transition-colors"
                    >
                        <Close fontSize="small" />
                    </button>
                </div>
            </div>
        )}
      </main>

      {/* Pagination */}
      <footer className="max-w-[1200px] mx-auto px-6 pb-20 pt-10 border-t border-slate-200 dark:border-slate-800 mt-10">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Showing {filteredAssets.length > 0 ? 1 : 0} to {filteredAssets.length} of {assets.length} assets</p>
          <div className="flex items-center gap-2">
            <button className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" disabled>
              <ChevronLeft fontSize="small" />
            </button>
            <button className="w-9 h-9 bg-primary text-white rounded-lg text-sm font-bold shadow-sm">1</button>
            <button className="w-9 h-9 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-bold transition-colors">2</button>
            <button className="w-9 h-9 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-sm font-bold transition-colors">3</button>
            <button className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <ChevronRight fontSize="small" />
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};
